import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AxiosInstance from "../Home/AxiosInstance";
import {
  storeTokens,
  buildTraineeSignInPayload,
  buildTraineeOtpLoginRequestPayload,
  buildTraineeOtpLoginVerifyPayload,
  normalizeMobileNo,
} from "../Home/auth";
import "./TraineeSignIn.css";
import "../Home/publicPages.css";
import "../Home/authPages.css";

const BASE_PATH = (process.env.REACT_APP_BASE_PATH || "/als").replace(/\/+$/, "");

const isMobileLogin = (value) => /^[\d\s+()-]+$/.test(value);

export default function TraineeSignIn() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("password"); // password | otp
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [formData, setFormData] = useState({
    login: "",
    password: "",
    mobileNo: "",
    otp: "",
  });

  const handleChange = (e) => {
    const { id } = e.target;
    let { value } = e.target;
    if (id === "mobileNo" || (id === "login" && isMobileLogin(value))) {
      value = normalizeMobileNo(value).slice(0, 10);
    }
    setFormData((prev) => ({ ...prev, [id]: value }));
    setError("");
    if (id === "mobileNo") {
      setOtpSent(false);
      setInfo("");
      setFormData((prev) => ({ ...prev, otp: "", mobileNo: value }));
    }
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setError("");
    setInfo("");
    setOtpSent(false);
    setFormData((prev) => ({ ...prev, otp: "" }));
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password", {
      state: {
        userType: "trainee",
      },
    });
  };

  const completeSignIn = (data) => {
    if (data?.trainee?.trainee_id) {
      storeTokens(data);
      localStorage.setItem("traineeId", data.trainee.trainee_id);
      navigate("/trainee-profile");
      return true;
    }
    setError("Unexpected response from server.");
    return false;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    const login = formData.login.trim();
    if (!login || !formData.password) {
      setError("Please enter email or mobile number and password.");
      return;
    }
    if (isMobileLogin(login) && !/^\d{10}$/.test(login)) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await AxiosInstance.post(
        "companies/signin",
        buildTraineeSignInPayload(login, formData.password)
      );
      completeSignIn(response.data);
    } catch (err) {
      console.error("Sign-in error:", err);
      setError(
        err.response?.data?.error || "Invalid credentials. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    const mobile_no = normalizeMobileNo(formData.mobileNo);
    if (mobile_no.length !== 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    setLoading(true);
    setError("");
    setInfo("");
    try {
      const response = await AxiosInstance.post(
        "companies/otp/login/request/",
        buildTraineeOtpLoginRequestPayload(mobile_no)
      );
      setFormData((prev) => ({ ...prev, mobileNo: mobile_no }));
      setOtpSent(true);
      setInfo(
        response.data?.message || "OTP sent successfully to your phone number"
      );
    } catch (err) {
      console.error("OTP request error:", err);
      const data = err.response?.data;
      setError(
        data?.error ||
          data?.mobile_no?.[0] ||
          data?.message ||
          "Failed to send OTP. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    const mobile_no = normalizeMobileNo(formData.mobileNo);
    const otp = String(formData.otp || "").trim();
    if (mobile_no.length !== 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (!/^\d{6}$/.test(otp)) {
      setError("Please enter the 6-digit OTP.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await AxiosInstance.post(
        "companies/otp/login/verify/",
        buildTraineeOtpLoginVerifyPayload(mobile_no, otp)
      );
      completeSignIn(response.data);
    } catch (err) {
      console.error("OTP verify error:", err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Invalid OTP. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="als-auth-page trainee-auth-page">
      <div className="als-auth-shell">
        <aside className="als-auth-brand">
          <div className="als-auth-brand-inner">
            <p className="als-auth-eyebrow">Learner portal</p>
            <h1>Learn. Grow. Achieve.</h1>
            <p>
              Sign in to access your courses, track progress, and explore
              internships on Open Learnings.
            </p>
            <ul className="als-auth-points">
              <li>
                <span>✓</span> Continue your enrolled courses
              </li>
              <li>
                <span>✓</span> Discover new skills and programs
              </li>
              <li>
                <span>✓</span> Apply to internship opportunities
              </li>
            </ul>
          </div>
        </aside>
        <div className="als-auth-panel">
          <div className="auth-forms-container">
            <div className="signin-container">
              <h3>Welcome back</h3>
              <p className="als-auth-lead">
                Sign in to your learner account to continue.
              </p>

              {mode === "password" ? (
                <form onSubmit={handlePasswordSubmit}>
                  <div className="mb-3">
                    <label htmlFor="login" className="form-label">
                      Email or mobile number
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="login"
                      placeholder="Enter email or mobile number"
                      value={formData.login}
                      onChange={handleChange}
                      autoComplete="username"
                      disabled={loading}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">
                      Password
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="password"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleChange}
                      disabled={loading}
                      required
                    />
                  </div>

                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}

                  <div className="als-auth-inline-links mb-3">
                    <span
                      className="green-hover-link1"
                      style={{ cursor: "pointer" }}
                      onClick={handleForgotPassword}
                    >
                      Forgot Password?
                    </span>
                    <span
                      className="green-hover-link1"
                      style={{ cursor: "pointer" }}
                      onClick={() => switchMode("otp")}
                    >
                      Login with OTP
                    </span>
                  </div>

                  <button type="submit" className="btn1" disabled={loading}>
                    {loading ? "Signing in…" : "Sign In"}
                  </button>
                </form>
              ) : (
                <form
                  onSubmit={otpSent ? handleOtpVerify : handleRequestOtp}
                >
                  <div className="mb-3">
                    <label htmlFor="mobileNo" className="form-label">
                      Mobile number
                    </label>
                    <input
                      type="tel"
                      className="form-control"
                      id="mobileNo"
                      placeholder="10-digit mobile number"
                      value={formData.mobileNo}
                      onChange={handleChange}
                      autoComplete="tel"
                      inputMode="numeric"
                      maxLength={10}
                      disabled={loading}
                      required
                    />
                  </div>

                  {otpSent ? (
                    <div className="mb-3">
                      <label htmlFor="otp" className="form-label">
                        OTP
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="otp"
                        placeholder="6-digit OTP"
                        value={formData.otp}
                        onChange={(e) => {
                          const digits = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 6);
                          setFormData((prev) => ({ ...prev, otp: digits }));
                          setError("");
                        }}
                        inputMode="numeric"
                        maxLength={6}
                        disabled={loading}
                        required
                      />
                    </div>
                  ) : null}

                  {info && (
                    <div className="alert alert-success" role="status">
                      {info}
                    </div>
                  )}
                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}

                  <div className="als-auth-inline-links mb-3">
                    <span
                      className="green-hover-link1"
                      style={{ cursor: "pointer" }}
                      onClick={() => switchMode("password")}
                    >
                      Sign in with password
                    </span>
                    {otpSent ? (
                      <button
                        type="button"
                        className="btn btn-link green-hover-link1 p-0"
                        disabled={loading}
                        onClick={handleRequestOtp}
                      >
                        Resend OTP
                      </button>
                    ) : (
                      <span />
                    )}
                  </div>

                  <button type="submit" className="btn1" disabled={loading}>
                    {loading
                      ? otpSent
                        ? "Verifying…"
                        : "Sending…"
                      : otpSent
                        ? "Verify & Sign In"
                        : "Send OTP"}
                  </button>
                </form>
              )}

              <div className="als-auth-footer-links">
                <small>
                  Don&apos;t have an account?{" "}
                  <a
                    href={`${BASE_PATH}/trainee-signup`}
                    className="green-hover-link1"
                  >
                    Sign Up
                  </a>
                </small>
                <small>
                  <Link to="/" className="green-hover-link1">
                    Home
                  </Link>
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
